import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getAdminSupportChatPath } from "../../utils/supportContact";
import { TextField } from "../common/inputFiled";
import { appStorage } from "../../utils/appStorage";
import {
  finishPostSignupIntro,
  isPostSignupIntroActive,
} from "../../utils/postSignupOnboarding";

const GUIDE_PAGE_CLASS =
  "rounded-2xl border border-gray-100 p-5 shadow-sm md:p-6 max-md:pb-[calc(1.5rem+var(--mobile-nav-offset,5rem))]";

const ChevronRight = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const GuideBackHeader = ({ title, subtitle, onBack }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onBack}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white border border-gray-100 shadow-sm hover:bg-gray-50"
        aria-label="Go back"
      >
        <img src="./back-arrow.svg" alt="" className="h-4 w-4" />
      </button>
      <h2 className="flex-1 text-center text-lg font-bold text-[#1E1F29] uppercase tracking-wide pr-9">{title}</h2>
    </div>
    {subtitle ? (
      <p className="mt-3 text-center text-sm leading-relaxed text-[#7C8090]">{subtitle}</p>
    ) : null}
  </div>
);

const GUIDE_ACTIONS = [
  { label: "Call", icon: "./call-color.svg" },
  { label: "EMAIL", icon: "./input-logos/sms.svg" },
  { label: "SMS", icon: "./message-new.svg" },
  { label: "Add Contact", icon: "./add-contact.svg" },
  { label: "Friend Request", icon: "./add-friend-color.svg" },
]
const GUIDE_ACTIONS2 = [
  { label: "Invite Contact", icon: "./share-color.svg" },
  { label: "Pin Group", icon: "./move-color.svg" },
  { label: "Group Info", icon: "./info1.svg" },
  { label: "Selection", icon: "./selection.svg" },
  { label: "Notification", icon: "./color-notification.svg" },
]
const GUIDE_ACTIONS3 = [
  { label: "Add Pending Request", icon: "./chatbar/check.svg" },
  { label: "Delivery waiting message", icon: "./status-new.svg" },
];

const GUIDE_PERMISSION_ITEMS = [
  {
    key: "Tags",
    title: "Tags",
    description: "Categorize and manage your contacts.",
  },
  {
    key: "Status",
    title: "Status",
    description: "Understand contact status and updates.",
  },
  {
    key: "Permission",
    title: "Permission",
    description: "Manage access and user permissions.",
  },
];

const TAG_GUIDE_DATA = {
  Family: {
    description: "This tag is for your family members and close relatives.",
    examples: [
      "Parents and siblings",
      "Spouse and children",
      "Grandparents and cousins",
      "In-laws and extended family",
      "Family caregivers",
      "Household members",
    ],
  },
  Friends: {
    description: "This tag is for your friends and people you know personally.",
    examples: [
      "Close friends and best friends",
      "School or college friends",
      "Neighbors",
      "Social and casual friends",
      "Gym buddies",
      "Travel buddies",
    ],
  },
  Work: {
    description: "This tag is for colleagues, clients, and professional contacts.",
    examples: [
      "Coworkers and teammates",
      "Managers and reports",
      "Clients and vendors",
      "Business partners",
      "Industry contacts",
      "Professional mentors",
    ],
  },
  Other: {
    description: "This tag is for contacts who do not fit Family, Friends, or Work.",
    examples: [
      "Acquaintances",
      "Service providers",
      "Community contacts",
      "One-time connections",
      "General contacts",
      "Miscellaneous contacts",
    ],
  },
};

const PERMISSION_MATRIX_ROWS = [
  { label: "First Name", field: "first_name" },
  { label: "Last Name", field: "last_name" },
  { label: "Personal Email", field: "personal_email_id" },
  { label: "Work Email", field: "work_email_id" },
  { label: "Personal Number", field: "personal_mobile" },
  { label: "Work Number", field: "work_mobile" },
  { label: "Date of Birth (DD/MM)", fields: ["date_of_birth_DD_MM"] },
  { label: "Date of Birth (YYYY)", fields: ["date_of_birth_YYYY"] },
];

const GUIDE_TAG_COLUMNS = [
  { label: "Family", displayKey: "Family" },
  { label: "Friends", displayKey: "Friend" },
  { label: "Work", displayKey: "Work" },
  { label: "Other", displayKey: "Others" },
];

const DEMO_PERMISSION_DEFAULTS = {
  Friend: {
    first_name: 2,
    last_name: 2,
    personal_email_id: 1,
    work_email_id: 1,
    personal_mobile: 1,
    work_mobile: 0,
    date_of_birth_DD_MM: 1,
    date_of_birth_YYYY: 1,
  },
  Family: {
    first_name: 2,
    last_name: 2,
    personal_email_id: 1,
    work_email_id: 0,
    personal_mobile: 1,
    work_mobile: 0,
    date_of_birth_DD_MM: 1,
    date_of_birth_YYYY: 1,
  },
  Work: {
    first_name: 2,
    last_name: 2,
    personal_email_id: 0,
    work_email_id: 1,
    personal_mobile: 0,
    work_mobile: 1,
    date_of_birth_DD_MM: 0,
    date_of_birth_YYYY: 0,
  },
  Other: {
    first_name: 0,
    last_name: 0,
    personal_email_id: 0,
    work_email_id: 0,
    personal_mobile: 0,
    work_mobile: 0,
    date_of_birth_DD_MM: 0,
    date_of_birth_YYYY: 0,
  },
};

const getDemoRelationKey = (displayKey) => (displayKey === "Others" ? "Other" : displayKey);

const displayKeyToTagOption = (displayKey) => (displayKey === "Others" ? "Other" : displayKey);

const DEMO_PREVIEW_DATA = {
  first_name: "Manish",
  last_name: "Kumar",
  personal_email_id: "manish@gmail.com",
  work_email_id: "manish.work@company.com",
  personal_mobile: "+91 9876543210",
  work_mobile: "+91 9123456789",
  date_of_birth_DD_MM: "08/07",
  date_of_birth_YYYY: "1996",
};

const PERMISSION_PREVIEW_FIELDS = [
  { key: "first_name", label: "First Name", icon: "./input-logos/user.svg" },
  { key: "last_name", label: "Last Name", icon: "./input-logos/user.svg" },
  { key: "personal_email_id", label: "Personal Email", icon: "./input-logos/sms.svg" },
  { key: "work_email_id", label: "Work Email", icon: "./input-logos/sms.svg" },
  { key: "personal_mobile", label: "Personal Number", icon: "./input-logos/call.svg" },
  { key: "work_mobile", label: "Work Number", icon: "./input-logos/call.svg" },
  { key: "date_of_birth", label: "Date of Birth (DD/MM)", icon: "./input-logos/calander.svg", fields: ["date_of_birth_DD_MM"] },
  { key: "date_of_birth_YYYY", label: "Date of Birth (YYYY)", icon: "./input-logos/calander.svg", fields: ["date_of_birth_YYYY"] },
];

const getDemoRelationPermissions = (permissions, displayKey) => {
  if (displayKey === "Others") return permissions.Other || {};
  return permissions[displayKey] || {};
};

const isDemoFieldReadOnly = (value) => Number(value) === 2;
const isDemoFieldSelected = (value) => {
  const normalized = Number(value);
  return normalized === 1 || normalized === 2;
};

const getGuideFieldState = (permissions, displayKey, row) => {
  const relationPermissions = getDemoRelationPermissions(permissions, displayKey);
  const fieldKeys = row.fields || [row.field];
  const values = fieldKeys.map((fieldKey) => Number(relationPermissions[fieldKey] ?? 0));
  const checked =
    fieldKeys.length === 1
      ? isDemoFieldSelected(values[0])
      : values.every((value) => isDemoFieldSelected(value));
  const locked = values.some((value) => isDemoFieldReadOnly(value));

  return { checked, locked };
};

const PermissionGuideCheckbox = ({ checked, locked, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={locked}
    aria-pressed={checked}
    className={`inline-flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
      locked ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-[#6C45FF]"
    } ${checked ? "bg-[#6C45FF] border-[#6C45FF] text-white" : "border-gray-300 bg-white"}`}
  >
    {checked ? (
      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
  </button>
);

const STATUS_AUDIENCE_RULE_OPTIONS = [
  { id: "family", label: "Family", tags: ["Family"] },
  { id: "friend", label: "Friend", tags: ["Friend"] },
  { id: "work", label: "Work", tags: ["Work"] },
  { id: "other", label: "Other", tags: ["Other"] },
  { id: "family-friend", label: "Family + Friend", tags: ["Family", "Friend"] },
  { id: "family-work", label: "Family + Work", tags: ["Family", "Work"] },
  { id: "friend-work", label: "Friend + Work", tags: ["Friend", "Work"] },
  { id: "family-friend-work", label: "Family + Friend + Work", tags: ["Family", "Friend", "Work"] },
];

const STATUS_DEMO_CONTACTS = [
  { id: "dummy-1", name: "John Doe", tags: ["Friend"], lastMessage: "" },
  { id: "dummy-2", name: "Jane Smith", tags: ["Family", "Work"], lastMessage: "" },
  { id: "dummy-3", name: "Sarah Lee", tags: ["Family", "Friend"], lastMessage: "" },
  { id: "dummy-4", name: "Alex Johnson", tags: ["Other"], lastMessage: "" },
];

const formatStatusOfflineMessage = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) {
    return "User Offline. Message delivery and replies will be delayed.";
  }

  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes] = timeStr.split(":").map(Number);
  const dt = new Date(year, month - 1, day, hours, minutes);

  const datePart = new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  })
    .format(dt)
    .toUpperCase();

  const timePart = new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  })
    .format(dt)
    .toUpperCase();

  return `User Offline until ${datePart} and ${timePart} IST. Message delivery and replies will be delayed.`;
};

const contactMatchesAudienceRule = (contactTags, ruleTags) =>
  ruleTags.every((tag) => contactTags.includes(tag));

const contactSeesOfflineStatus = (contactTags, selectedOptionIds) =>
  selectedOptionIds.some((optionId) => {
    const option = STATUS_AUDIENCE_RULE_OPTIONS.find((item) => item.id === optionId);
    if (!option) return false;
    return contactMatchesAudienceRule(contactTags, option.tags);
  });

const statusAudienceTagButtonClass = (isSelected) =>
  `flex h-11 w-full items-center justify-center rounded-full border-2 px-3 text-center text-xs font-medium leading-tight transition-all duration-200 sm:text-sm ${
    isSelected
      ? "border-[#6938EF] bg-[#6938EF] text-white"
      : "border-gray-300 bg-white text-gray-700 hover:border-[#6938EF]"
  }`;

const StatusChatPreviewContact = ({ contact, showStatusMessage, statusMessage }) => (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
    <img src="./profile-picture.png" alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-[#1E1F29]">{contact.name}</p>
      {showStatusMessage ? (
        <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-[#6938EF]">{statusMessage}</p>
      ) : (
        <p className="mt-0.5 truncate text-xs text-[#7C8090]">{contact.lastMessage}</p>
      )}
      <div className="mt-1.5 flex flex-wrap gap-1">
        {contact.tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-[#F3F0FF] px-2 py-0.5 text-[10px] font-medium text-[#6C45FF]"
          >
            {friendTagIcon(tag)}
            {tag}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const DUMMY_FRIEND_PROFILE = {
  name: "John Doe",
  phone: "+91 9876543210",
  email: "dummy@email.com",
};

const tagOptionToGuideKey = (tag) => (tag === "Friend" ? "Friends" : tag);

const tagGuideMainButtonClass = (active) =>
  `flex w-full items-center justify-center rounded-2xl border px-3 py-4 text-sm font-semibold transition sm:text-base ${
    active
      ? "border-[#7C4DFF] bg-[#7C4DFF] text-white"
      : "border-[#E5E7EB] bg-white text-[#1E1F29] hover:border-[#D6C5FF]"
  }`;

const tagCategoryTabClass = (active) =>
  `inline-flex shrink-0 items-center rounded-full justify-center  border px-3 py-1 text-[11px] font-medium leading-none transition  sm:px-3.5 sm:py-1.5 sm:text-xs ${
    active
      ? "border-[#7C4DFF] bg-[#7C4DFF] text-white"
      : "border-[#E5E7EB] bg-white text-[#1E1F29] hover:border-[#D6C5FF]"
  }`;

const FRIEND_TAG_OPTIONS = ["Family", "Friend", "Work", "Other"];

const friendTagIcon = (tag) => {
  switch ((tag || "").toLowerCase()) {
    case "family":
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2 20c0-2.5 4-4.5 10-4.5s10 2 10 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "friend":
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 20c0-3 3.5-5 7-5s7 2 7 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "work":
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="7" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "other":
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="16.5" r="0.5" fill="currentColor" />
        </svg>
      );
    default:
      return null;
  }
};

const DummyFriendProfileCard = ({ selectedTags }) => (
  <div className="rounded-2xl bg-[#F3F0FF] px-5 py-4">
    <div className="flex items-center gap-4">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#C4C4C4]">
        <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-base font-bold text-[#1E1F29]">{DUMMY_FRIEND_PROFILE.name}</p>
        {selectedTags.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {selectedTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex rounded-full bg-[#7C4DFF] px-3 py-0.5 text-xs font-semibold text-white"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}
        <p className="text-sm text-[#1E1F29]">{DUMMY_FRIEND_PROFILE.phone}</p>
        <p className="text-sm text-[#1E1F29] break-all">{DUMMY_FRIEND_PROFILE.email}</p>
      </div>
    </div>
  </div>
);

const toggleFriendTags = (prev, tag) => {
  const normalizedTag = String(tag || "").toLowerCase();
  const isOtherTag = normalizedTag === "other";

  if (isOtherTag) {
    const alreadyOnlyOther = prev.length === 1 && prev.some((t) => String(t || "").toLowerCase() === "other");
    return alreadyOnlyOther ? [] : [tag];
  }

  const withoutOther = prev.filter((t) => String(t || "").toLowerCase() !== "other");
  const exists = withoutOther.includes(tag);
  return exists ? withoutOther.filter((t) => t !== tag) : [...withoutOther, tag];
};

const PermissionTagSelector = ({ selectedTags, previewTag, onToggleTag }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-4 gap-2">
      {FRIEND_TAG_OPTIONS.map((tag) => {
        const active = selectedTags.includes(tag);
        const isPreview = previewTag === tag && active;
        return (
          <button
            key={tag}
            type="button"
            onClick={() => onToggleTag(tag)}
            aria-pressed={active}
            className={`flex w-full flex-col items-center justify-center gap-1 rounded-xl px-2 py-2.5 text-xs sm:text-sm font-medium transition ${
              active
                ? isPreview
                  ? "bg-[#6C45FF] text-white shadow-md ring-2 ring-[#D6C5FF] ring-offset-1"
                  : "bg-[#6C45FF] text-white shadow-md"
                : "border border-gray-300 bg-white text-gray-800 hover:border-[#D6C5FF]"
            }`}
          >
            <span className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-gray-500"}`}>
              {friendTagIcon(tag)}
            </span>
            <span className="truncate w-full text-center">{tag}</span>
          </button>
        );
      })}
    </div>
    <p className="text-xs text-gray-400 text-center">You can select multiple tags (except Other)</p>
  </div>
);

const isFieldVisibleForRelation = (permissions, relation, fieldConfig) => {
  const relationPermissions = permissions[relation] || {};
  const fieldKeys = fieldConfig.fields || [fieldConfig.key];
  return fieldKeys.every((fieldKey) => isDemoFieldSelected(relationPermissions[fieldKey] ?? 0));
};

const getPermissionPreviewValue = (fieldConfig) => {
  if (fieldConfig.fields) {
    const parts = fieldConfig.fields.map((key) => DEMO_PREVIEW_DATA[key]).filter(Boolean);
    if (fieldConfig.key === "date_of_birth" && parts.length === 2) {
      return `${parts[0]}/${parts[1]}`;
    }
    return parts.join(" ");
  }
  return DEMO_PREVIEW_DATA[fieldConfig.key] || "";
};

const PermissionPreviewSection = ({ selectedTags, permissions }) => {
  const visibleFields = PERMISSION_PREVIEW_FIELDS.filter((field) =>
    selectedTags.some((tag) => {
      const relation = tag === "Other" ? "Other" : tag;
      return isFieldVisibleForRelation(permissions, relation, field);
    })
  );

  const previewLabel = selectedTags.join(", ");

  return (
    <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden shadow-[1px_4px_4px_0_rgba(122,90,248,0.08)]">
      <div className="px-4 py-3 border-b border-gray-100 bg-[#FAF8FF]">
        <h3 className="text-base font-bold text-[#1E1F29]">How contact see your information</h3>
        {selectedTags.length > 0 ? (
          <p className="text-xs text-[#7C8090] mt-0.5">
            Preview for{" "}
            <span className="font-semibold text-[#6C45FF]">{previewLabel}</span>
            {selectedTags.length > 1 ? " tags" : " tag"}
          </p>
        ) : null}
      </div>
      {selectedTags.length === 0 || visibleFields.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-[#7C8090]">
          {selectedTags.length === 0
            ? "Select a tag above to see what information is shared."
            : "No fields are visible for the selected tags. Enable permissions in the table above."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 px-4 pb-4">
          {visibleFields.map((field) => (
            <div key={field.key} className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 text-[#6938EF]">
                <img src={field.icon} alt="" className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="text-xs text-gray-500">{field.label}</div>
                <div className="text-sm font-medium text-gray-900 break-words">
                  {getPermissionPreviewValue(field) || "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FriendTagSelector = ({ selectedTags, onToggleTag }) => (
  <div className="space-y-4">
    <h4 className="text-center text-base font-bold text-[#1E1F29] sm:text-lg">
      How you know {DUMMY_FRIEND_PROFILE.name}
    </h4>
    <div className="grid grid-cols-4 gap-2 sm:gap-3">
      {FRIEND_TAG_OPTIONS.map((tag) => {
        const active = selectedTags.includes(tag);
        return (
          <button
            key={tag}
            type="button"
            onClick={() => onToggleTag(tag)}
            aria-pressed={active}
            className={tagGuideMainButtonClass(active)}
          >
            <span className={`h-5 w-5 mr-2 shrink-0 ${active ? "text-white" : "text-gray-500"}`}>
                  {friendTagIcon(tag)}
                </span>
                <span className="truncate">{tag}</span>
          </button>
        );
      })}
    </div>
    <p className="text-center text-xs text-[#9CA3AF]">You can select multiple tags (except Other)</p>
  </div>
);

const TagCategoryPanel = ({ activeTag, onTagChange }) => {
  const guideKey = tagOptionToGuideKey(activeTag);
  const tagData = TAG_GUIDE_DATA[guideKey];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {FRIEND_TAG_OPTIONS.map((tag) => {
          const active = activeTag === tag;
          return (
            <button
              key={tag}
              type="button"
              onClick={() => onTagChange(tag)}
              aria-pressed={active}
              className={tagCategoryTabClass(active)}
            >
              {tag}
            </button>
          );
        })}
      </div>

      <div className="relative mt-3">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-1 bottom-0 top-2 rounded-2xl border border-[#ECE8FF] bg-white"
        />
        <div className="relative rounded-2xl border border-[#E5E7EB] bg-white px-4 pb-1 pt-0 sm:px-5">
          <h3 className="py-3.5 text-sm font-bold text-[#1E1F29] sm:text-base">{activeTag}</h3>
          <ul>
            {tagData.examples.map((example) => (
              <li
                key={example}
                className="border-t border-[#EEEEEE] py-1 text-[13px] leading-snug text-[#1E1F29] sm:text-sm"
              >
                {example}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const GuideContactBar = () => (
  <div className="mb-4 w-[40%] rounded-2xl border border-gray-100 bg-white p-3 shadow-[1px_4px_4px_0_rgba(122,90,248,0.08)]">
    <div className="flex w-full items-center gap-3 rounded-[100px] border border-gray-100 bg-[#797C7B] bg-opacity-[11%] p-3 py-1 shadow-xl">
      <div className="relative shrink-0">
        <img
          src="/profile-picture.png"
          alt=""
          className="h-12 w-12 rounded-full object-cover"
        />
      </div>

      <div className="min-w-0 flex-1">
        <p className="heading-font max-w-full line-clamp-1 text-left">Alex Johnson</p>
        <p className="body-font text-left text-[#757575]">Available</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <span className="grid place-items-center" title="Call" aria-hidden="true">
          <img src="../chatbox/call.svg" alt="" className="h-5 w-5 opacity-70" />
        </span>
        <span className="grid place-items-center" title="Mail" aria-hidden="true">
          <img src="../input-logos/sms.svg" alt="" className="h-5 w-5 opacity-70" />
        </span>
        <span className="grid place-items-center" title="Phonebook" aria-hidden="true">
          <img src="../chatbox/phonebook.svg" alt="" className="h-5 w-5 opacity-70" />
        </span>
      </div>
    </div>
  </div>
);

const GuideMenuRow = ({ title, description, onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group flex w-full items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-white px-4 py-4 text-left shadow-[1px_4px_4px_0_rgba(122,90,248,0.06)] transition hover:border-[#D6C5FF] hover:bg-[#FAF8FF] ${className}`}
  >
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-bold text-[#1E1F29]">{title}</span>
      <span className="block text-xs text-[#7C8090] mt-0.5">{description}</span>
    </span>
    <ChevronRight className="h-4 w-4 shrink-0 text-[#1E1F29] transition group-hover:translate-x-0.5" />
  </button>
);

const GuideHelpCta = () => {
  const navigate = useNavigate();

  const handleClick = () => {
    const supportChatPath = getAdminSupportChatPath();
    if (supportChatPath === "/chat") {
      toast.error("Unable to open support chat. Please try again.");
      return;
    }
    navigate(supportChatPath);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group flex w-full items-center gap-3 rounded-2xl bg-[#fff] px-4 py-4 text-left transition hover:bg-[#EDE7FF]"
      aria-label="Contact support"
    >
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-[#1E1F29]">Contact to Coustomer Support</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-[#6C45FF] transition group-hover:translate-x-0.5" />
    </button>
  );
};

const UserGuideMain = ({ onNavigate, showPostSignupSkip, onPostSignupSkip }) => (
  <div className={GUIDE_PAGE_CLASS}>
    <div className="mb-6 flex items-start justify-between gap-3">
      <h2 className="flex-1 text-lg font-bold text-[#1E1F29] uppercase tracking-wide text-center">User Guide</h2>
      {showPostSignupSkip ? (
        <button
          type="button"
          onClick={onPostSignupSkip}
          className="shrink-0 rounded-full border border-[#D6C5FF] bg-white px-4 py-2 text-sm font-semibold text-[#6C45FF] shadow-sm transition hover:bg-[#FAF8FF]"
        >
          Skip
        </button>
      ) : null}
    </div>

    <div className="mb-8">
      <h3 className="text-sm font-bold text-[#1E1F29] uppercase mb-1">Actions</h3>
      <p className="text-xs text-[#7C8090] mb-4">Quick actions to manage your contacts.</p>
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 bg-white rounded-2xl">
        {GUIDE_ACTIONS.map((action) => (
          <div
            key={action.label}
            className="flex flex-col items-center gap-2 rounded-2xl bg-white p-3"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F3F0FF]">
              <img src={action.icon} alt="" className="h-5 w-5 object-contain " />
            </span>
            <span className="text-[10px] sm:text-xs font-semibold text-[#1E1F29] text-center leading-tight uppercase">
              {action.label}
            </span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 bg-white rounded-2xl">
        {GUIDE_ACTIONS2.map((action) => (
          <div
            key={action.label}
            className="flex flex-col items-center gap-2 rounded-2xl bg-white p-3"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F3F0FF]">
              <img src={action.icon} alt="" className="h-5 w-5 object-contain " />
            </span>
            <span className="text-[10px] sm:text-xs font-semibold text-[#1E1F29] text-center leading-tight uppercase">
              {action.label}
            </span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 bg-white rounded-2xl">
        {GUIDE_ACTIONS3.map((action) => (
          <div
            key={action.label}
            className="flex flex-col items-center gap-2 rounded-2xl bg-white p-3"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F3F0FF]">
              <img src={action.icon} alt="" className="h-5 w-5 object-contain " />
            </span>
            <span className="text-[10px] sm:text-xs font-semibold text-[#1E1F29] text-center leading-tight uppercase">
              {action.label}
            </span>
          </div>
        ))}
      </div>
      </div>
    </div>

    <div className="mb-6">
    <h3 className="text-sm font-bold text-[#1E1F29] uppercase mb-4">Contact Details</h3>

      <GuideContactBar />
      <h3 className="text-sm font-bold text-[#1E1F29] uppercase mb-4">Learn more about permissions and settings.
      </h3>
    {/* <p className="text-xs text-[#7C8090] mb-4">Learn more about permissions and settings.</p>      <p className="text-xs text-[#7C8090] mb-4">Learn more about features and settings.</p> */}
      <div className="space-y-3">
        {GUIDE_PERMISSION_ITEMS.map((item) => (
          <GuideMenuRow
            key={item.key}
            title={item.title}
            description={item.description}
            onClick={() => onNavigate(item.key)}
          />
        ))}
      </div>
    </div>

    {showPostSignupSkip ? (
      <div className="mb-4 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onPostSignupSkip}
          className="submit-button w-full max-w-[230px] !px-5 !py-2.5 text-sm font-semibold shadow-sm transition hover:opacity-90"
        >
          Continue to profile setup
        </button>
        <button
          type="button"
          onClick={onPostSignupSkip}
          className="w-full max-w-[190px] rounded-full border border-[#8862F2] bg-white px-5 py-2.5 text-center text-sm font-semibold text-[#6C45FF] shadow-sm transition hover:bg-[#F7F4FF]"
        >
          Skip introduction
        </button>
      </div>
    ) : null}

    <GuideHelpCta />
  </div>
);

const TagsGuide = ({ onBack, onNavigateToPermission }) => {
  const [selectedFriendTags, setSelectedFriendTags] = useState(["Friend"]);
  const [categoryTab, setCategoryTab] = useState("Friend");

  const handleFriendTagToggle = (tag) => {
    setSelectedFriendTags((prev) => toggleFriendTags(prev, tag));
  };

  return (
    <div className={GUIDE_PAGE_CLASS}>
      <GuideBackHeader
        title="Tags"
        subtitle=""
        onBack={onBack}
      />

      <div className="mb-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-[1px_4px_4px_0_rgba(122,90,248,0.08)] sm:p-6">
        <h3 className="mb-5 text-base font-bold text-[#1E1F29] sm:text-lg">
          How to change tags for your contacts
        </h3>
        <div className="space-y-6">
          <DummyFriendProfileCard selectedTags={selectedFriendTags} />
          <FriendTagSelector selectedTags={selectedFriendTags} onToggleTag={handleFriendTagToggle} />
        </div>
      </div>

      <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-[1px_4px_4px_0_rgba(122,90,248,0.06)] sm:p-5">
        <TagCategoryPanel activeTag={categoryTab} onTagChange={setCategoryTab} />
      </div>

      <GuideMenuRow
        title="Permissions"
        description="Manage what each tag can access about you"
        onClick={onNavigateToPermission}
        className="mt-4"
      />
    </div>
  );
};

const StatusGuide = ({ onBack }) => {
  const timeInputRef = useRef(null);
  const [enableUntil, setEnableUntil] = useState(true);
  const [statusDate, setStatusDate] = useState("2026-07-24");
  const [statusTime, setStatusTime] = useState("22:15");
  const [statusText, setStatusText] = useState("");
  const [savedStatusText, setSavedStatusText] = useState("");
  const [savedEnableUntil, setSavedEnableUntil] = useState(false);
  const [savedAudienceOptions, setSavedAudienceOptions] = useState([]);
  const [savedStatusDate, setSavedStatusDate] = useState("");
  const [savedStatusTime, setSavedStatusTime] = useState("");
  const [selectedAudienceOptions, setSelectedAudienceOptions] = useState(["friend"]);

  const previewOfflineMessage = formatStatusOfflineMessage(savedStatusDate, savedStatusTime);

  const isAudienceOptionSelected = (optionId) => selectedAudienceOptions.includes(optionId);

  const handleSaveStatus = () => {
    if (enableUntil) {
      if (selectedAudienceOptions.length === 0) {
        toast.error("Select at least one audience tag.");
        return;
      }
      if (!statusDate || !statusTime) {
        toast.error("Select date and time.");
        return;
      }
      setSavedAudienceOptions([...selectedAudienceOptions]);
      setSavedStatusDate(statusDate);
      setSavedStatusTime(statusTime);
      setSavedEnableUntil(true);
      setSavedStatusText("");
      toast.success("Status saved for preview");
      return;
    }

    const trimmed = statusText.trim();
    if (!trimmed) {
      toast.error("Enter a status message.");
      return;
    }
    setSavedStatusText(trimmed);
    setSavedEnableUntil(false);
    setSavedAudienceOptions([]);
    toast.success("Status saved for preview");
  };

  const toggleAudienceRule = (optionId) => {
    setSelectedAudienceOptions((prev) => {
      if (optionId === "other") {
        return prev.includes("other") ? [] : ["other"];
      }

      const withoutOther = prev.filter((id) => id !== "other");
      return withoutOther.includes(optionId)
        ? withoutOther.filter((id) => id !== optionId)
        : [...withoutOther, optionId];
    });
  };

  return (
    <div className={GUIDE_PAGE_CLASS}>
      <GuideBackHeader title="Status" onBack={onBack} />

      <div className="rounded-2xl border border-gray-100 bg-white p-2 px-5 mb-4 shadow-[1px_4px_4px_0_rgba(122,90,248,0.08)]">
        <div className="flex items-center gap-2 mb-2">
          <span className="h-2 w-2 rounded-full bg-[#6C45FF]" />
          <h3 className="text-base font-bold text-[#1E1F29]">Set Your Status</h3>
        </div>
        <p className="text-sm text-[#7C8090]">
          Set a status message for your contacts, or schedule an offline-until time with audience tags.
        </p>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-4 sm:p-5 mb-4 shadow-[1px_4px_4px_0_rgba(122,90,248,0.08)]">
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-sm text-gray-500">Offline until</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={enableUntil}
              onChange={(e) => {
                const { checked } = e.target;
                if (!checked) {
                  setSelectedAudienceOptions([]);
                  setStatusDate("");
                  setStatusTime("");
                  setEnableUntil(false);
                  return;
                }
                setEnableUntil(true);
              }}
            />
            <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-[#6938EF] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-4 after:w-4 after:rounded-full after:transition-all peer-checked:after:translate-x-[16px]" />
          </label>
        </label>

        {enableUntil && (
          <>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField
                label="Date"
                type="date"
                name="status-demo-date"
                value={statusDate}
                startYear={new Date().getFullYear()}
                endYear={new Date().getFullYear() + 10}
                onChange={(e) => setStatusDate(e.target.value)}
                startIcon="../input-logos/calander.svg"
              />

              <div className="space-y-1">
                <label className="block text-xs font-medium input-lable my-3">Time</label>
                <div className="relative">
                  <img
                    src="../input-logos/clock.svg"
                    alt=""
                    className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2"
                  />
                  <input
                    ref={timeInputRef}
                    type="time"
                    name="status-demo-time"
                    value={statusTime}
                    onChange={(e) => setStatusTime(e.target.value)}
                    onClick={() => timeInputRef.current?.showPicker?.()}
                    className="w-full h-11 rounded-[13px] border border-[#E6E2FF] bg-white py-3 pl-10 pr-10 text-gray-800 outline-none shadow-[inset_0_0_0_1px_rgba(108,69,255,0.15)] transition focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10"
                  />
                </div>
              </div>
            </div>

            <div className="my-6 space-y-5">
              <p className="text-xs text-[#7C8090]">
                Select audience tags to preview who sees your offline message.
              </p>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-3 block">Single Tags</label>
                <div className="grid grid-cols-2 min-[360px]:grid-cols-4 gap-2 sm:gap-3">
                  {STATUS_AUDIENCE_RULE_OPTIONS.filter((option) => option.tags.length === 1).map((option) => {
                    const isSelected = isAudienceOptionSelected(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleAudienceRule(option.id)}
                        className={statusAudienceTagButtonClass(isSelected)}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-3 block">Dual Tags</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3">
                  {STATUS_AUDIENCE_RULE_OPTIONS.filter((option) => option.tags.length === 2).map((option) => {
                    const isSelected = isAudienceOptionSelected(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleAudienceRule(option.id)}
                        className={statusAudienceTagButtonClass(isSelected)}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-3 block">Triple Tag</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {STATUS_AUDIENCE_RULE_OPTIONS.filter((option) => option.tags.length === 3).map((option) => {
                    const isSelected = isAudienceOptionSelected(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleAudienceRule(option.id)}
                        className={statusAudienceTagButtonClass(isSelected)}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}

        {!enableUntil && (
          <>
            <label className="text-sm font-semibold text-gray-700 mb-3 block mt-4">Status</label>
            <textarea
              value={statusText}
              onChange={(e) => setStatusText(e.target.value)}
              onFocus={(e) => {
                window.setTimeout(() => {
                  e.target.scrollIntoView({ block: "center", behavior: "smooth" });
                }, 300);
              }}
              className="w-full h-36 sm:h-48 rounded-[13px] border bg-white px-4 py-3 outline-none transition focus:border-[#6938EF] focus:ring-4 focus:ring-[#6938EF]/10"
              placeholder="Enter your status (120 Characters)"
              maxLength={120}
            />
          </>
        )}

        <div className="mt-6 flex justify-center">
          <button type="button" onClick={handleSaveStatus} className="submit-button w-full sm:w-96">
            Set Status
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden mb-4 shadow-[1px_4px_4px_0_rgba(122,90,248,0.08)]">
        <div className="px-4 py-3 border-b border-gray-100 bg-[#FAF8FF]">
          <h3 className="text-base font-bold text-[#1E1F29]">How contacts see your status in chat</h3>
          <p className="text-xs text-[#7C8090] mt-0.5">
            {savedEnableUntil
              ? "Contacts matching your selected tags will see your offline status instead of the last message."
              : "All contacts will see your status message instead of the last message."}
          </p>
        </div>

        <div>
          {STATUS_DEMO_CONTACTS.map((contact) => {
            const showStatusMessage = savedEnableUntil
              ? contactSeesOfflineStatus(contact.tags, savedAudienceOptions)
              : Boolean(savedStatusText);
            const statusMessage = savedEnableUntil ? previewOfflineMessage : savedStatusText;
            return (
              <StatusChatPreviewContact
                key={contact.id}
                contact={contact}
                showStatusMessage={showStatusMessage}
                statusMessage={statusMessage}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PermissionGuide = ({ onBack }) => {
  const [permissions, setPermissions] = useState(() => structuredClone(DEMO_PERMISSION_DEFAULTS));
  const [selectedTags, setSelectedTags] = useState(["Friend"]);
  const [previewTag, setPreviewTag] = useState("Friend");

  const handlePermissionTagToggle = (tag) => {
    setSelectedTags((prev) => {
      const next = toggleFriendTags(prev, tag);
      if (next.includes(tag)) {
        setPreviewTag(tag);
      } else {
        setPreviewTag((current) => (current === tag ? next[0] || "" : current));
      }
      return next;
    });
  };

  const handleGuideToggle = (displayKey, row, tagLabel) => {
    const tagOption = displayKeyToTagOption(displayKey);
    if (!selectedTags.includes(tagOption)) {
      toast.error(`Please select ${tagLabel} tag first`);
      return;
    }

    const relation = getDemoRelationKey(displayKey);
    const relationPermissions = getDemoRelationPermissions(permissions, displayKey);
    const fieldKeys = row.fields || [row.field];
    const values = fieldKeys.map((fieldKey) => Number(relationPermissions[fieldKey] ?? 0));

    if (values.some((value) => isDemoFieldReadOnly(value))) return;

    const currentlyChecked =
      fieldKeys.length === 1
        ? isDemoFieldSelected(values[0])
        : values.every((value) => isDemoFieldSelected(value));
    const next = currentlyChecked ? 0 : 1;

    setPermissions((prev) => {
      const copy = structuredClone(prev);
      if (!copy[relation]) copy[relation] = {};
      fieldKeys.forEach((fieldKey) => {
        copy[relation][fieldKey] = next;
      });
      return copy;
    });
  };

  const effectivePreviewTag =
    previewTag && selectedTags.includes(previewTag) ? previewTag : selectedTags[0] || "";

  return (
    <div className={GUIDE_PAGE_CLASS}>
      <GuideBackHeader title="Permissions" onBack={onBack} />

      <div className="rounded-2xl border border-gray-100 bg-white p-5 mb-4 shadow-[1px_4px_4px_0_rgba(122,90,248,0.08)]">
        <div className="flex items-start gap-3 mb-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#6C45FF] text-white">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 3l7 4v5c0 4.4-2.9 8.5-7 9.8C7.9 20.5 5 16.4 5 12V7l7-4z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <h3 className="text-base font-bold text-[#1E1F29] pt-2">Select Tag for contact</h3>
        </div>
        <PermissionTagSelector
          selectedTags={selectedTags}
          previewTag={effectivePreviewTag}
          onToggleTag={handlePermissionTagToggle}
        />
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-4 mb-4 overflow-x-auto shadow-[1px_4px_4px_0_rgba(122,90,248,0.08)]">
        <h3 className="text-base font-bold text-[#1E1F29] mb-3 px-1">Select Permission for contact</h3>
        <table className="w-full min-w-[480px] text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-3 pr-4 font-semibold text-[#1E1F29]">Contact Details</th>
              {GUIDE_TAG_COLUMNS.map((tag) => {
                const editable = selectedTags.includes(displayKeyToTagOption(tag.displayKey));
                return (
                  <th
                    key={tag.label}
                    className={`py-3 px-2 text-center font-semibold text-[#1E1F29] ${
                      editable ? "bg-[#F3F0FF]" : ""
                    }`}
                  >
                    {tag.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_MATRIX_ROWS.map((row) => (
              <tr key={row.label} className="border-b border-gray-50 last:border-0">
                <td className="py-3 pr-4 text-[#1E1F29]">{row.label}</td>
                {GUIDE_TAG_COLUMNS.map((tag) => {
                  const { checked, locked } = getGuideFieldState(permissions, tag.displayKey, row);
                  const editable = selectedTags.includes(displayKeyToTagOption(tag.displayKey));
                  return (
                    <td
                      key={tag.label}
                      className={`py-3 px-2 text-center ${editable ? "bg-[#FAF8FF]/60" : ""}`}
                    >
                      <PermissionGuideCheckbox
                        checked={checked}
                        locked={locked}
                        onToggle={() => handleGuideToggle(tag.displayKey, row, tag.label)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mb-4">
        <PermissionPreviewSection selectedTags={selectedTags} permissions={permissions} />
      </div>
    </div>
  );
};

export const IntroductionPage = ({ activeSubTab, onSubTabChange }) => {
  const showPostSignupSkip = isPostSignupIntroActive();

  const handlePostSignupSkip = () => {
    // End Introduction onboarding → Profile Completion modal → Personal Info
    finishPostSignupIntro();
    try {
      appStorage.setItem("active_section", "Personal Info");
      appStorage.removeItem("active_setting_subsection");
      appStorage.removeItem("active_introduction_subtab");
      appStorage.removeItem("active_permission_subtab");
    } catch (e) {
      // ignore
    }
    try {
      window.dispatchEvent(new CustomEvent("phonebook:post-signup-go-profiles"));
    } catch (e) {
      // ignore
    }
  };

  if (activeSubTab === "Tags") {
    return (
      <TagsGuide
        onBack={() => onSubTabChange(null)}
        onNavigateToPermission={() => onSubTabChange("Permission")}
      />
    );
  }
  if (activeSubTab === "Status") {
    return <StatusGuide onBack={() => onSubTabChange(null)} />;
  }
  if (activeSubTab === "Permission") {
    return <PermissionGuide onBack={() => onSubTabChange(null)} />;
  }
  return (
    <UserGuideMain
      onNavigate={onSubTabChange}
      showPostSignupSkip={showPostSignupSkip}
      onPostSignupSkip={handlePostSignupSkip}
    />
  );
};
